import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ReorderResizeTable from 'components/ReorderResizeTable';
import AnalyticsCard from '../AnalyticsCard';
import {
  HEADING_TRANSLATIONS,
  SIZE_VALUES,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/data';
import { BODY_CONTENT_GENERATE } from './components';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import InfiniteScroll from 'components/InfiniteScroll';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { EnterpriseMember } from 'models/Enterprises';
import { Button, Input } from 'ncoded-component-library';
import { useNavigate } from 'react-router-dom';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { getEnterpriseRoute } from 'utils';
import SearchIcon from 'icons/Search.icon';
import useQueryFilter from 'hooks/useQueryFilter';
import { COLLEAGUE_ID } from '../../../../../Portfolio/components/PortfolioSellerFilters/data';
import useDebounceSearch from 'hooks/useDebounceSearch';

import './TopPerformingBrokers.styles.scss';
import './TopPerformingBrokers.styles.responsive.scss';

const LIMIT = '10';

type TopPerformingBrokersProps = {
  className?: string;
};

const TopPerformingBrokers: FC<TopPerformingBrokersProps> = ({ className }) => {
  const [activeSort, setActiveSort] = useState(`-${SORTING_VALUES.totalDeals}`);

  const { enterprise } = useContext(EnterpriseContext);

  const { searchString, searchInputValue, onSearchInputChange } =
    useDebounceSearch();

  const { onFiltersApplyAndNavigate } = useQueryFilter();

  const classes = classNames('top-performing-brokers', className);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    loading,
    items: brokers,
    onContainerScrolled,
  } = useInfinitePagination<EnterpriseMember>({
    condition: !!enterprise?._id,
    resetDeps: [enterprise?._id, activeSort, searchString],
    dependencies: [enterprise?._id, activeSort, searchString],
    makeRequest: async (currentPage) =>
      api.enterpriseAnalytics
        .getTopPerformingBrokers(enterprise?._id, {
          $page: currentPage.toString(),
          $limit: LIMIT,
          $sort: activeSort,
          $searchString: searchString,
        })
        .then(({ data }) => data),
  });

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (member: EnterpriseMember) =>
            BODY_CONTENT_GENERATE[col]({ member }),
        }),
        {},
      ),
    [],
  );

  const navigateToDealsPage = useCallback(
    ({ user }: EnterpriseMember) =>
      onFiltersApplyAndNavigate('../deals', COLLEAGUE_ID, user?._id),
    [onFiltersApplyAndNavigate],
  );

  return (
    <AnalyticsCard
      title={t('mostActive')}
      className={classes}
      action={
        <>
          <Input
            prefixNode={<SearchIcon />}
            placeholder={t('search')}
            value={searchInputValue}
            onChange={({ target: { value } }) => onSearchInputChange(value)}
          />
          <Button
            variant="outline"
            className="top-performing-brokers__action"
            icon={ArrowrightIcon}
            iconPosition="right"
            onClick={() =>
              navigate(
                getEnterpriseRoute(false, false, enterprise?._id, 'team'),
              )
            }
          >
            {t('viewAll')}
          </Button>
        </>
      }
    >
      {!loading && !brokers.length ? (
        <ReportsEmptyState />
      ) : (
        <InfiniteScroll
          role="listbox"
          localScroll
          className="top-performing-brokers__scroll"
          onScroll={onContainerScrolled}
        >
          <ReorderResizeTable
            isDragDisabled
            isResizeDisabled
            sort={activeSort}
            columns={TABLE_COLUMNS}
            data={brokers}
            isDataLoading={loading}
            sortingValues={SORTING_VALUES}
            customSizes={SIZE_VALUES}
            headingTranslations={HEADING_TRANSLATIONS}
            tableContentTransformations={renderedContent}
            updateSort={setActiveSort}
            onRowClick={navigateToDealsPage}
          />
        </InfiniteScroll>
      )}
    </AnalyticsCard>
  );
};

export default TopPerformingBrokers;
